/*
 * Copyright (c) All right reserved Anvish Inc. 2025
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */

"use client";

import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import Image from "next/image";
import { Button } from "@heroui/button";
import "swiper/css";
import { Link } from "@heroui/link";

interface BranchSliderProps {
  branchData: {
    country: string;
    image: {
      meta: {
        download_url: string;
      };
      title: string;
    };
    link: string;
  }[];
}

const BranchSlider: React.FC<BranchSliderProps> = ({ branchData }) => {
  const sliderRef = React.useRef<any>(null);
  const [isReachedEnd, setIsReachedEnd] = useState<boolean>(false);
  const [isReachedStart, setIsReachedStart] = useState(false);

  const handlePrev = () => {
    sliderRef.current.swiper.slidePrev();
  };

  const handleNext = () => {
    sliderRef.current.swiper.slideNext();
  };

  // Extreme End Position Checker
  useEffect(() => {
    if (sliderRef.current?.swiper) {
      const swiper = sliderRef.current.swiper;

      setIsReachedStart(swiper.isBeginning);
      setIsReachedEnd(swiper.isEnd);

      // Listen for changes in swiper state
      swiper.on("slideChange", () => {
        setIsReachedStart(swiper.isBeginning);
        setIsReachedEnd(swiper.isEnd);
      });
    }
  }, [sliderRef]);

  return (
    <div className="flex flex-col w-full relative">
      <Swiper
        ref={sliderRef}
        breakpoints={{
          640: {
            slidesPerView: 3,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 5,
            spaceBetween: 30,
          },
        }}
        className="w-full h-full"
        slidesPerView={2}
        spaceBetween={20}
      >
        {branchData.map((branch, index: number) => (
          <SwiperSlide
            key={index}
            className="!flex w-fit h-full items-center justify-center"
          >
            <Link
              className="!flex flex-col w-fit h-full items-center justify-center gap-4 md:gap-6 shrink-0"
              href={branch.link}
            >
              <Image
                key={index}
                alt={branch.image.title}
                className="aspect-square w-[120px] h-[120px] md:w-[150px] md:h-[150px] lg:w-[180px] lg:h-[180px] object-cover rounded-full overflow-clip"
                height={180}
                src={branch.image.meta.download_url}
                width={180}
              />
              <p className="text-xl md:text-2xl lg:text-3xl font-medium text-ax-text-color">
                {branch.country}
              </p>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="flex lg:hidden w-full items-center justify-center gap-3 md:gap-4 mt-6">
        <Button
          isIconOnly
          className="w-10 h-10 bg-[#F6F6F6] rounded-full"
          isDisabled={isReachedStart}
          radius="full"
          variant="flat"
          onPress={handlePrev}
        >
          <img
            alt="Left Arrow"
            className="w-5 h-5 object-contain rotate-180"
            height={20}
            src="/icons/arrow_left_black.svg"
            width={20}
          />
        </Button>
        <Button
          isIconOnly
          className="w-10 h-10 bg-[#F6F6F6] rounded-full"
          isDisabled={isReachedEnd}
          radius="full"
          variant="flat"
          onPress={handleNext}
        >
          <img
            alt="Left Arrow"
            className="w-5 h-5 object-contain"
            height={20}
            src="/icons/arrow_left_black.svg"
            width={20}
          />
        </Button>
      </div>
    </div>
  );
};

export default BranchSlider;
